import { ArticleService } from 'api/services/ArticleService'
import { useThrottledFn } from 'beautiful-react-hooks'
import cx from 'classnames'
import { Spin } from 'common/Spin/Spin'
import { clamp } from 'lodash'
import { Unsplash } from 'proseShared/IntegrationsIcons'
import { MediaUploadPromptContent } from 'proseShared/prompts/MediaUploadPrompt/MediaUploadPrompt'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { ArticleMeta, SelectedImage } from 'types'
import { optimizedImageUrlString } from 'utils'
import { currentlyInCore } from 'utils/articleUtils'
import Styles from './CoverPhoto.module.scss'

interface Props {
  articleMeta: ArticleMeta
  editAccess?: boolean
}

export const CoverPhoto = ({ articleMeta, editAccess = false }: Props) => {
  const [loading, setLoading] = useState(true)
  const [promptVisibility, setPromptVisibility] = useState(false)
  const [repositioning, setRepositioning] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [verticalPosition, setVerticalPosition] = useState<{ height: number; imagePosition: number }>({
    height: 1,
    imagePosition: articleMeta?.cover_image_position ?? 50,
  })
  const [dirtyVerticalPosition, setDirtyVerticalPosition] = useState(0)

  const inCore = currentlyInCore(articleMeta)
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleOnRemove = async () => {
    ArticleService.updateArticle(articleMeta.id, { cover_image_url: null })
  }

  const handleOnImageChange = (images?: SelectedImage[]) => {
    if (!images?.length) {
      setPromptVisibility(false)
      return
    }

    setLoading(true)
    ArticleService.updateArticle(articleMeta.id, { cover_image_url: images[0].url, cover_image_position: null })
  }

  const handleOnPositionChange = () => {
    ArticleService.updateArticle(articleMeta.id, { cover_image_position: verticalPosition.imagePosition })
    setRepositioning(false)
  }

  const handleRepositioning = useCallback(
    (e: MouseEvent) => {
      if (repositioning) {
        const myImage = imageRef.current
        if (!myImage) return
        const bbox = myImage.getBoundingClientRect()

        // this prevents from having position out of image's bounds. Without it a user could
        // reposition an image indefinitely and see a white background eventually

        setVerticalPosition({
          height: bbox.height,
          imagePosition: clamp(e.movementY * 0.6 + verticalPosition.imagePosition, 0, 100),
        })
      }
    },
    [repositioning, verticalPosition],
  )

  const handleRepositioningRef = useRef(handleRepositioning)

  useEffect(() => {
    handleRepositioningRef.current = handleRepositioning
  }, [dragging, handleRepositioning])

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    const myImage = imageRef.current

    if (!myImage || !repositioning) return

    setDragging(true)
    document.addEventListener('mousemove', dragCheck)
  }

  const dragCheck = useThrottledFn((e: MouseEvent) => {
    if (e.buttons === 0) {
      setDragging(false)
      document.removeEventListener('mousemove', dragCheck)
    } else {
      /*
        We keep a reference to handleRepositioning, and call that reference instead.
        Simply calling handleRepositioning() without keeping the refs up to date will result in
        calling a stale version of that function, the one created in the first render, and that
        version will not see changes to the params (i.e. `dragging` will always be false)
      */
      handleRepositioningRef.current(e)
    }
  }, 16)

  const handleDragEnd = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(false)
    document.removeEventListener('mousemove', dragCheck)
  }

  // if (!articleMeta?.cover_image_url) return null
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const cover_image_url = optimizedImageUrlString(articleMeta.cover_image_url!)

  const startRepositioning = () => {
    setRepositioning(true)
    setDirtyVerticalPosition(verticalPosition.imagePosition)
  }

  const cancelRepositioning = () => {
    setRepositioning(false)
    setVerticalPosition({ imagePosition: dirtyVerticalPosition, height: verticalPosition.height })
  }

  return (
    <div
      className={cx(Styles.container, {
        [Styles.repositioning]: repositioning,
        [Styles.dragging]: dragging,
        [Styles.inCore]: inCore,
      })}
      data-test-id="cover-photo"
    >
      {loading && <Spin className={Styles.spinner} />}
      <div className={Styles.imageContainer} ref={containerRef}>
        <img
          ref={imageRef}
          src={cover_image_url}
          className={Styles.image}
          draggable={false}
          alt="Cover banner"
          onMouseUp={handleDragEnd}
          onMouseDown={handleDragStart}
          onLoad={() => setLoading(false)}
          style={{ objectPosition: `center ${verticalPosition.imagePosition}%` }}
        />
      </div>

      {editAccess && (
        <div
          className={cx(Styles.controlsContainer, { [Styles.repositioning]: repositioning })}
          data-test-id="cover-photo-controls"
        >
          {repositioning && (
            <div className={Styles.repositioningInfo}>
              <span>Drag Image to Reposition</span>
            </div>
          )}

          <div className={Styles.buttonsGroupContainer}>
            {!loading && (
              <div className={Styles.buttonsGroup}>
                {!repositioning && (
                  <>
                    <button className={Styles.actionButton} onClick={() => setPromptVisibility(true)}>
                      Change Image
                    </button>

                    <button className={Styles.actionButton} onClick={startRepositioning}>
                      Reposition
                    </button>

                    <button
                      className={Styles.actionButton}
                      onClick={handleOnRemove}
                      aria-label="Remove Banner Image"
                      id="removeBannerImage"
                    >
                      Remove
                    </button>
                  </>
                )}

                {repositioning && (
                  <>
                    <button className={Styles.actionButton} onClick={handleOnPositionChange}>
                      Save Position
                    </button>

                    <button className={Styles.actionButton} onClick={cancelRepositioning}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}

            {promptVisibility && (
              <MediaUploadPromptContent
                multiSelect={false}
                onSubmit={handleOnImageChange}
                preselectedIntegration={Unsplash}
                classNames={Styles.mediaPrompt}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
